import Grid from '@mui/material/Grid';
import ShipmentsSupplierDetailPanel from 'components/DetailPanels/ShipmentsSupplierDetailPanel';
import BaseTable from 'components/Tables/BaseTable';
import { alertError } from 'ducks/alerts';
import { actions } from 'ducks/suppliers';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import React from 'react';
import { connect } from 'react-redux';
import docDefinition from 'reports/inventory/suppliers';
import { createSelector } from 'reselect';
import { getImmutableStoreData, shipmentsByRawMaterial } from 'selectors/generic';
import exportXLSX from 'utils/export/rawMaterials';
import tableIcons from 'utils/icons/materialTableIcons';
import columns from 'utils/tables/columns/inventory/supplier';
import noIcon from 'utils/tables/noIcon';
import supplierValidator from 'utils/tables/validators/suppliers';
import { currentUser } from 'selectors/currentUser';

pdfMake.addVirtualFileSystem(pdfFonts);

/**
 * The shipments of each supplier.
 *
 * It used to be built here, in the render, by walking every raw material and
 * its nested shipments. The walk is kept - raw materials in slice order, then
 * that raw material's shipments - rather than grouping the shipments slice
 * directly, because the order it produces is the order the panel lists them in.
 */
const shipmentsBySupplier = createSelector(
  [state => state.rawMaterials.data, shipmentsByRawMaterial],
  (rawMaterials, shipmentsOf) => rawMaterials.reduce((acc, rm) => {
    (shipmentsOf[rm.id] || []).forEach(shipment => {
      if (shipment.supplier_id) {
        if (acc[shipment.supplier_id]) {
          acc[shipment.supplier_id].push(shipment);
        } else {
          acc[shipment.supplier_id] = [shipment];
        }
      }
    });
    return acc;
  }, {})
);

const Component = props => {
  const { shipmentsBySupplierId, suppliers, suppliersById, rawMaterials, user } = props;
  const { alertError, create, remove, update } = props;
  const tableRef = React.useRef(null);

  return (
    <Grid size={12}>
      <BaseTable
        ref={tableRef}
        columns={columns(user)}
        data={suppliers}
        onRowClick={() => null}
        actions={[
          {
            isFreeAction: true,
            icon: tableIcons.Pdf,
            tooltip: 'Relatório PDF',
            onClick: (event, rowData) => {
              const filteredTableData = tableRef.current.state.data;
              pdfMake.createPdf(docDefinition(filteredTableData)).open();
            }
          },
          {
            isFreeAction: true,
            icon: tableIcons.Xlsx,
            tooltip: 'Exportar XLSX',
            onClick: exportXLSX(columns(user), tableRef, 'FORNECEDORES.xlsx')
          }
        ]}
        detailPanel={[
          rowData => !shipmentsBySupplierId[rowData.id] || shipmentsBySupplierId[rowData.id].length === 0 ?
            noIcon :
            {
              icon: tableIcons.Shipments,
              tooltip: 'Fornecimentos',
              render: () => <ShipmentsSupplierDetailPanel rowData={shipmentsBySupplierId[rowData.id]} data={suppliersById} title={'Fornecimentos'}/>
            }
        ]}
        editable={{
          onRowAdd: newData => {
            const validation = supplierValidator(newData, suppliers);
            if (validation.error) {
              alertError(validation.error);
              return Promise.reject();
            } else {
              return create(validation);
            }
          },
          onRowUpdate: (newData, oldData) => {
            const validation = supplierValidator(newData, suppliers);
            if (validation.error) {
              alertError(validation.error);
              return Promise.reject();
            } else {
              return update(newData.id, newData);
            }

          },
          onRowDelete: oldData => {
            const supplierUsed = (shipmentsBySupplierId[oldData.id] || []).length > 0;
            if (supplierUsed) {
              alertError('Não pode apagar o Fornecedor pois está a ser usado em Fornecimentos.');
              return Promise.resolve();
            } else {
              return remove(oldData.id);
            }
          }
        }}
      />
    </Grid>
  );
};

const makeMapStateToProps = () => {
  const immutableSuppliers = getImmutableStoreData('suppliers');
  const mapStateToProps = (state, props) => ({
    rawMaterials: state.rawMaterials.data,
    shipmentsBySupplierId: shipmentsBySupplier(state),
    suppliers: immutableSuppliers(state),
    suppliersById: state.suppliers.models,
    user: currentUser(state)
  });
  return mapStateToProps;
};

const mapDispatchToProps = dispatch => ({
  alertError: error => dispatch(alertError(error)),
  create: data => dispatch(actions.createPromise(data)),
  remove: id => dispatch(actions.removePromise(id)),
  update: (id, data) => dispatch(actions.updatePromise(id, data))
});

export default connect(makeMapStateToProps, mapDispatchToProps)(Component);
